import React from 'react';
import { Form, message, Modal } from 'antd';
import { observer } from 'mobx-react';

import QuoteStore from '../../stores/QuoteStore';
import QuoteRequirementsEditor from './QuoteRequirementsEditor';
import RequirementsApplyBar from './RequirementsApplyBar';
import { useQuoteRequirementsDraft } from './useQuoteRequirementsDraft';

interface Props {
    open: boolean;
    onClose: () => void;
}

export const QuoteRequirementsModal = observer(({ open, onClose }: Props) => {
    const [form] = Form.useForm();
    const draft = useQuoteRequirementsDraft({
        form,
        onApply: payload => QuoteStore.update_quote_fields(payload),
    });

    const close = () => {
        draft.discard();
        onClose();
    };

    const requestClose = () => {
        if (draft.applying) return;
        if (!draft.changes.count) {
            close();
            return;
        }

        Modal.confirm({
            title: 'Discard unsaved changes?',
            content: 'Your changes to the requirements will be lost.',
            okText: 'Discard changes',
            cancelText: 'Keep editing',
            className: 'quote-requirements-confirm',
            autoFocusButton: 'cancel',
            onOk: close,
        });
    };

    const apply = async () => {
        if (await draft.apply()) {
            message.success('Requirements saved. Prices recalculated.');
            onClose();
        }
    };

    return (
        <Modal
            className="quote-requirements-modal"
            closable={!draft.applying}
            destroyOnClose
            footer={
                <RequirementsApplyBar
                    alwaysShowSecondary
                    draft={draft}
                    onApply={apply}
                    onSecondary={requestClose}
                    secondaryLabel="Cancel"
                />
            }
            keyboard={!draft.applying}
            maskClosable={!draft.applying}
            onCancel={requestClose}
            open={open}
            style={{ top: 24 }}
            title="Requirements"
            width="min(640px, calc(100vw - 32px))"
        >
            <div data-testid="quote-requirements-modal">
                <Form form={form} layout="vertical" preserve={false}>
                    <QuoteRequirementsEditor draft={draft} />
                </Form>
            </div>
        </Modal>
    );
});

export default QuoteRequirementsModal;
